import { useEffect, useState } from 'react';
import { BUILD, checkForUpdate, formatBuilt, hardRefresh, useUpdate } from '../app/version';
import { api } from '../sync/api';
import { PanelFrame } from './floating';

type Health = { version: string; php: string; db?: string };

/** Build, server and update status, with a manual check and a hard refresh. */
export function AboutPanel({ onClose }: { onClose: () => void }) {
  const available = useUpdate((s) => s.available);
  const checking = useUpdate((s) => s.checking);
  const checkedAt = useUpdate((s) => s.checkedAt);
  const error = useUpdate((s) => s.error);
  const [health, setHealth] = useState<Health | 'unreachable' | null>(null);
  const [sw, setSw] = useState<'active' | 'none' | 'unsupported'>('unsupported');

  useEffect(() => {
    api
      .get<Health>('/health')
      .then(setHealth)
      .catch(() => setHealth('unreachable'));
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .getRegistrations()
        .then((regs) => setSw(regs.length ? 'active' : 'none'))
        .catch(() => setSw('none'));
    }
  }, []);

  const updateLine = !import.meta.env.PROD
    ? 'dev build, not checked'
    : available
      ? `newer build on the server (built ${formatBuilt(available.built)})`
      : checking
        ? 'checking…'
        : error
          ? `check failed: ${error}`
          : checkedAt
            ? 'up to date'
            : 'not checked yet';

  return (
    <PanelFrame kind="about" title="About VDS" className="about-panel" onClose={onClose}>
      <div className="about-body">
        <dl className="kv about-kv">
          <dt>App</dt>
          <dd>{BUILD.version}</dd>
          <dt>Build</dt>
          <dd>
            {formatBuilt(BUILD.built)}
            <br />
            <span className="muted">commit {BUILD.commit}</span>
          </dd>
          <dt>Server</dt>
          <dd>
            {health === null ? '…' : health === 'unreachable' ? 'unreachable' : `${health.version} · PHP ${health.php}`}
          </dd>
          <dt>Offline shell</dt>
          <dd>{sw === 'active' ? 'service worker active' : sw === 'none' ? 'no service worker' : 'not supported'}</dd>
          <dt>Update</dt>
          <dd className={available ? 'good' : undefined}>{updateLine}</dd>
        </dl>
        <div className="about-actions">
          {available ? (
            <button type="button" className="btn btn-primary" onClick={() => void hardRefresh()}>
              Reload to update
            </button>
          ) : (
            <button type="button" className="btn" disabled={checking || !import.meta.env.PROD} onClick={() => void checkForUpdate()}>
              Check for updates
            </button>
          )}
          <button
            type="button"
            className="btn"
            title="Drops the cached app shell and reloads from the server. Your boards are not affected"
            onClick={() => void hardRefresh()}
          >
            Hard refresh
          </button>
        </div>
        <p className="muted about-hint">
          The app checks for a new build when it starts, when you come back to it, and every 15 minutes. A hard refresh
          clears the cached shell only; boards, images and settings stay.
        </p>
      </div>
    </PanelFrame>
  );
}
